<script setup>
import { ref, computed } from 'vue';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import { ATTACHMENT_TYPES } from '../constants';
// NOTICE: KAPTION-INTEGRATION - image-preview-race: retry a failed load before giving up
import { useKaptionMediaRetry } from 'dashboard/composables/useKaptionMediaRetry';

const emit = defineEmits(['error']);
const showGallery = ref(false);
const { filteredCurrentChatAttachments, attachments } = useMessageContext();

const attachment = computed(() => {
  return attachments.value[0];
});

// NOTICE: KAPTION-INTEGRATION - image-preview-race: a just-sent video can be broadcast a moment
// before its file is in storage, so the first load may miss. Retry with backoff, then give up.
const {
  src: videoSrc,
  retryKey: videoKey,
  onError: handleError,
} = useKaptionMediaRetry(() => attachment.value?.dataUrl, {
  onGiveUp: () => emit('error'),
});

const isReel = computed(() => {
  return attachment.value.fileType === ATTACHMENT_TYPES.IG_REEL;
});

// NOTICE: KAPTION-INTEGRATION - the reel's caption, when Meta sent one with the share
const reelTitle = computed(() => {
  if (!isReel.value) return '';
  return (
    attachment.value.fallbackTitle ?? attachment.value.fallback_title ?? ''
  );
});
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-3"
    data-bubble-name="video"
    @click="showGallery = true"
  >
    <div class="relative group rounded-lg overflow-hidden">
      <div
        v-if="isReel"
        class="absolute p-2 flex items-start justify-end right-0 pointer-events-none"
      >
        <Icon icon="i-lucide-instagram" class="text-white shadow-lg" />
      </div>
      <!-- NOTICE: KAPTION-INTEGRATION - image-preview-race: retrying src -->
      <video
        :key="videoKey"
        controls
        class="rounded-lg skip-context-menu max-h-60"
        :src="videoSrc"
        :class="{
          'max-w-48': isReel,
          'max-w-full': !isReel,
        }"
        @error="handleError"
      />
    </div>
    <p
      v-if="reelTitle"
      class="mt-2 max-w-48 text-xs text-n-slate-11 line-clamp-3 whitespace-pre-line"
      :title="reelTitle"
    >
      {{ reelTitle }}
    </p>
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="onError"
    @close="() => (showGallery = false)"
  />
</template>
